<template>
  <div class="page-col">
    <div class="page-main">
      <div class="list-header-premium d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-0 mb-3" :class="{ 'bg-dark border-secondary': isDark }">
        <div class="d-flex align-items-center p-3 flex-shrink-0">
          <div class="header-icon-wrapper mr-3 ml-2" style="background: linear-gradient(135deg, #20a8d8 0%, #1783ae 100%);">
            <i class="fa fa-clipboard text-white"></i>
          </div>
          <div class="header-text-container">
            <h4 class="mb-0 font-weight-bold" :class="isDark ? 'text-white' : 'text-dark'">Inventário de Produtos</h4>
            <small class="text-muted mb-0 d-none d-lg-block">Abertura, lançamento e fechamento do inventário do estoque</small>
          </div>
        </div>

        <div class="d-flex align-items-center flex-wrap gap-2 p-3 w-100 w-md-auto justify-content-end">
          <div class="search-pill-responsive d-flex align-items-center bg-light rounded-pill p-1 shadow-xs border mr-2 flex-wrap">
            <div class="d-flex align-items-center px-2 mr-2 inventory-date-wrapper">
              <span class="text-muted small font-weight-bold mr-2 text-uppercase">Data</span>
              <cec-input-data
                ref="dataInventario"
                v-model="filtro.dataInventario"
                :disabled="inventarioAberto"
                input-class="rounded-pill"
                placeholderText="Informe a data"
              />
            </div>

            <b-button
              v-if="!inventarioAberto"
              variant="primary"
              pill
              class="px-4 font-weight-bold shadow-sm text-white btn-filtrar-responsive"
              :disabled="processando"
              @click="iniciarInventario()"
            >
              <i class="fa fa-play-circle mr-1"></i> INICIAR INVENTÁRIO
            </b-button>

            <template v-else>
              <div class="d-flex align-items-center px-2 border-left mr-2 inventory-filter-wrapper">
                <centro-estoque-drop-down
                  v-model="filtro.centroEstoqueId"
                  placeholder="Centro de estoque"
                  input-class="border-0 bg-transparent"
                />
              </div>
              <div class="d-flex align-items-center px-2 border-left mr-2 inventory-filter-wrapper">
                <b-form-input
                  v-model.trim="filtro.busca"
                  class="border-0 bg-transparent inventory-search-input"
                  placeholder="Buscar produto..."
                  @keydown.enter="aplicarFiltroCentro"
                ></b-form-input>
              </div>
              <b-button variant="primary" pill class="px-4 font-weight-bold shadow-sm text-white mr-2" :disabled="processando" @click="aplicarFiltroCentro">
                <i class="fa fa-search mr-1"></i> FILTRAR
              </b-button>
              <b-button variant="link" class="btn-icon-only text-muted" :disabled="processando" @click="resetarBusca" v-b-tooltip.hover title="Limpar busca">
                <i class="fa fa-ban"></i>
              </b-button>
              <b-button variant="link" class="btn-icon-only text-warning" :disabled="processando" @click="zerarTodos" v-b-tooltip.hover title="Zerar todos do centro">
                <i class="fa fa-eraser"></i>
              </b-button>
              <b-button variant="link" class="btn-icon-only text-success" :disabled="processando" @click="fecharInventario" v-b-tooltip.hover title="Fechar inventário">
                <i class="fa fa-check-square-o"></i>
              </b-button>
              <b-button variant="link" class="btn-icon-only text-info" :disabled="processando" @click="alterarData" v-b-tooltip.hover title="Selecionar outra data">
                <i class="fa fa-calendar"></i>
              </b-button>
            </template>
          </div>
        </div>
      </div>

      <b-overlay :show="processando" rounded="sm" opacity="0.35">
        <div v-if="!inventarioAberto" class="inventory-empty-state bg-white border rounded shadow-sm p-5 text-center">
          <div class="empty-state-icon mx-auto mb-3">
            <i class="fa fa-calendar-check-o"></i>
          </div>
          <h5 class="font-weight-bold text-dark mb-2">Informe a data para iniciar o inventário</h5>
          <p class="text-muted mb-0">Após a abertura, a tela libera o filtro por centro de estoque e a lista de itens para lançamento.</p>
        </div>

        <template v-else>
          <b-row class="mb-3">
            <b-col md="4" class="mb-3 mb-md-0">
              <div class="inventory-summary-card inventory-summary-primary">
                <small class="summary-label">Data do Inventário</small>
                <h4 class="mb-0 font-weight-bold">{{ formatarData(inventario.DT_INV) }}</h4>
              </div>
            </b-col>
            <b-col md="4" class="mb-3 mb-md-0">
              <div class="inventory-summary-card inventory-summary-warning">
                <small class="summary-label">Status</small>
                <h4 class="mb-0 font-weight-bold">{{ inventario.OP_STATUS_LABEL }}</h4>
              </div>
            </b-col>
            <b-col md="4">
              <div class="inventory-summary-card inventory-summary-success">
                <small class="summary-label">Itens Carregados</small>
                <h4 class="mb-0 font-weight-bold">{{ itensFiltrados.length }}</h4>
              </div>
            </b-col>
          </b-row>

          <div class="bg-white rounded border shadow-sm overflow-hidden">
            <b-table
              class="mb-0 table-finance"
              ref="tbl"
              :items="itensFiltrados"
              :fields="fields"
              :sticky-header="'68vh'"
              hover
              small
              bordered
              show-empty
              empty-text="Nenhum item encontrado para os filtros informados"
              responsive
            >
              <template #cell(NOME_PRODUTO)="data">
                <div class="font-weight-bold text-dark">{{ data.item.NOME_PRODUTO }}</div>
                <small class="text-muted">Produto {{ data.item.PRODUTO_ID }}</small>
              </template>

              <template #cell(CENTRO_ESTOQUE_ID)="data">
                <div class="font-weight-bold text-primary">{{ data.item.CENTRO_ESTOQUE_ID }}</div>
                <small class="text-muted">{{ data.item.NOME_CENTRO_ESTOQUE }}</small>
              </template>

              <template #cell(SALDO_FI)="data">
                {{ formatarNumero(data.item.SALDO_FI, 3) }}
              </template>

              <template #cell(SALDOFI_PECAS)="data">
                {{ formatarNumero(data.item.SALDOFI_PECAS, 0) }}
              </template>

              <template #cell(QUANT_ESTOQUE_ATUAL)="data">
                {{ formatarNumero(data.item.QUANT_ESTOQUE_ATUAL, 3) }}
              </template>

              <template #cell(PECAS_ESTOQUE_ATUAL)="data">
                {{ formatarNumero(data.item.PECAS_ESTOQUE_ATUAL, 0) }}
              </template>

              <template #cell(actions)="row">
                <div class="d-flex align-items-center gap-1 justify-content-end">
                  <b-button type="button" @click.stop="abrirModalItem(row.item)" variant="soft-info" pill class="btn-icon-only shadow-sm" title="Lançar inventário" v-b-tooltip.hover>
                    <i class="fa fa-pencil"></i>
                  </b-button>
                  <b-button type="button" @click.stop="zerarItem(row.item)" variant="soft-warning" pill class="btn-icon-only shadow-sm" title="Zerar item" v-b-tooltip.hover>
                    <i class="fa fa-eraser"></i>
                  </b-button>
                </div>
              </template>

              <template #custom-foot>
                <b-tr>
                  <b-td colspan="8" class="text-right font-weight-bold bg-light">
                    Total de registros: {{ itensFiltrados.length }}
                  </b-td>
                </b-tr>
              </template>
            </b-table>
          </div>
        </template>
      </b-overlay>
    </div>

    <inventario-produtos-item-modal ref="modalItem" @salvou="carregarInventario" />
  </div>
</template>

<script>
import CecInputData from '@/components/Cec/CecInputData.vue'
import CentroEstoqueDropDown from '@/views/componentes/dropdown/CentroEstoqueDropDown.vue'
import InventarioProdutosItemModal from './InventarioProdutosItemModal.vue'
import Tools from '@/tools/tools.js'

export default {
  name: 'InventarioProdutosCadastro',
  components: {
    CecInputData,
    CentroEstoqueDropDown,
    InventarioProdutosItemModal
  },

  data() {
    return {
      processando: false,
      inventario: {
        DT_INV: null,
        OP_STATUS: null,
        OP_STATUS_LABEL: '',
        TOTAL_ITENS: 0
      },
      itens: [],
      filtro: {
        dataInventario: Tools.getDataAtual(),
        centroEstoqueId: null,
        busca: ''
      },
      fields: [
        { key: 'SEQUENCIA', label: 'Seq.', thStyle: { width: '90px' }, tdClass: 'align-middle' },
        { key: 'NOME_PRODUTO', label: 'Produto', tdClass: 'align-middle' },
        { key: 'CENTRO_ESTOQUE_ID', label: 'Centro', tdClass: 'align-middle' },
        { key: 'SALDO_FI', label: 'Peso Inv.', thClass: 'text-right', tdClass: 'text-right align-middle' },
        { key: 'SALDOFI_PECAS', label: 'Peças Inv.', thClass: 'text-right', tdClass: 'text-right align-middle' },
        { key: 'QUANT_ESTOQUE_ATUAL', label: 'Peso Atual', thClass: 'text-right', tdClass: 'text-right align-middle' },
        { key: 'PECAS_ESTOQUE_ATUAL', label: 'Peças Atual', thClass: 'text-right', tdClass: 'text-right align-middle' },
        { key: 'actions', label: 'Ações', thClass: 'text-right', tdClass: 'text-right align-middle', thStyle: { width: '120px' } }
      ]
    }
  },

  computed: {
    isDark() {
      return localStorage.getItem('tema') === 'D'
    },
    inventarioAberto() {
      return !!this.inventario.DT_INV
    },
    itensFiltrados() {
      const busca = (this.filtro.busca || '').trim().toLowerCase()
      if (!busca) return this.itens

      return this.itens.filter((item) => {
        return [
          item.NOME_PRODUTO,
          item.PRODUTO_ID,
          item.CENTRO_ESTOQUE_ID,
          item.NOME_CENTRO_ESTOQUE
        ]
          .filter(Boolean)
          .some((valor) => String(valor).toLowerCase().includes(busca))
      })
    }
  },

  methods: {
    formatarData(data) {
      return Tools.formatarDataBR(data)
    },

    formatarNumero(valor, casas = 0) {
      return Number(valor || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: casas,
        maximumFractionDigits: casas
      })
    },

    validarDataInventario() {
      if (!this.filtro.dataInventario) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Informe a data de abertura do inventário.')
        this.$refs.dataInventario.focus()
        return false
      }

      const dataInformada = new Date(`${this.filtro.dataInventario}T00:00:00`)
      const hoje = new Date()
      hoje.setHours(0, 0, 0, 0)

      if (dataInformada > hoje) {
        Tools.exibirMensagem(this.$store, 'Data inválida', 'warning', 'A data Inventário não pode ser superior à data atual.')
        this.$refs.dataInventario.focus()
        return false
      }

      return true
    },

    async iniciarInventario(confirmarReabertura = false) {
      if (!this.validarDataInventario()) return

      this.processando = true

      try {
        const { data } = await this.$http.post('/api/protected/estoqueInventario/abrir', {
          dataInventario: this.filtro.dataInventario,
          confirmarReabertura
        })

        this.inventario = { ...(data.inventario || this.inventario) }
        this.itens = data.itens || []
        this.filtro.centroEstoqueId = null
        this.filtro.busca = ''
        Tools.exibirMensagem(this.$store, 'Sucesso', 'success', data.mensagem || 'Inventário aberto com sucesso!')
      } catch (err) {
        const mensagem = err.response?.data || err.message

        if (!confirmarReabertura && mensagem && mensagem.toLowerCase().includes('continuar abertura do inventário')) {
          const confirmar = await this.$bvModal.msgBoxConfirm(
            'O inventário para essa data já foi cadastrado. Continuar abertura do inventário?',
            {
              title: 'Inventário existente',
              okTitle: 'Continuar',
              cancelTitle: 'Cancelar',
              centered: true,
              headerClass: 'bg-warning text-dark'
            }
          )

          if (confirmar) {
            this.processando = false
            return this.iniciarInventario(true)
          }
        } else {
          Tools.exibirMensagem(this.$store, 'Erro', 'danger', mensagem)
        }
      } finally {
        this.processando = false
      }
    },

    async carregarInventario() {
      if (!this.filtro.dataInventario) return

      this.processando = true

      try {
        const { data } = await this.$http.get('/api/protected/estoqueInventario/getByData', {
          params: {
            dataInventario: this.filtro.dataInventario,
            centroEstoqueId: this.filtro.centroEstoqueId || null
          }
        })

        if (!data || !data.inventario) {
          this.inventario = { DT_INV: null, OP_STATUS: null, OP_STATUS_LABEL: '', TOTAL_ITENS: 0 }
          this.itens = []
          return
        }

        this.inventario = { ...data.inventario }
        this.itens = data.itens || []
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err.response?.data || err.message)
      } finally {
        this.processando = false
      }
    },

    async aplicarFiltroCentro() {
      if (!this.inventarioAberto) return
      await this.carregarInventario()
    },

    resetarBusca() {
      this.filtro.busca = ''
    },

    alterarData() {
      this.inventario = {
        DT_INV: null,
        OP_STATUS: null,
        OP_STATUS_LABEL: '',
        TOTAL_ITENS: 0
      }
      this.itens = []
      this.filtro.centroEstoqueId = null
      this.filtro.busca = ''

      this.$nextTick(() => {
        this.$refs.dataInventario.focus()
      })
    },

    abrirModalItem(item) {
      this.$refs.modalItem.abrir(item)
    },

    async zerarItem(item) {
      const confirmar = await this.$bvModal.msgBoxConfirm(
        `Zerar os valores do produto ${item.NOME_PRODUTO}?`,
        {
          title: 'Zerar item',
          okTitle: 'Zerar',
          cancelTitle: 'Cancelar',
          centered: true,
          headerClass: 'bg-warning text-dark'
        }
      )

      if (!confirmar) return

      this.processando = true

      try {
        const { data } = await this.$http.post('/api/protected/estoqueInventario/zerarItem', {
          sequencia: item.SEQUENCIA
        })

        Tools.exibirMensagem(this.$store, 'Sucesso', 'success', data.mensagem || 'Item zerado com sucesso!')
        await this.carregarInventario()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err.response?.data || err.message)
      } finally {
        this.processando = false
      }
    },

    async zerarTodos() {
      if (!this.filtro.centroEstoqueId) {
        Tools.exibirMensagem(this.$store, 'Aviso', 'warning', 'Informe o Centro de Estoque.')
        return
      }

      const confirmar = await this.$bvModal.msgBoxConfirm(
        'Zerar todos os itens do centro de estoque selecionado?',
        {
          title: 'Zerar todos os itens',
          okTitle: 'Zerar todos',
          cancelTitle: 'Cancelar',
          centered: true,
          headerClass: 'bg-warning text-dark'
        }
      )

      if (!confirmar) return

      this.processando = true

      try {
        const { data } = await this.$http.post('/api/protected/estoqueInventario/zerarTodos', {
          dataInventario: this.filtro.dataInventario,
          centroEstoqueId: this.filtro.centroEstoqueId
        })

        Tools.exibirMensagem(this.$store, 'Sucesso', 'success', data.mensagem || 'Itens zerados com sucesso!')
        await this.carregarInventario()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err.response?.data || err.message)
      } finally {
        this.processando = false
      }
    },

    async fecharInventario() {
      const confirmar = await this.$bvModal.msgBoxConfirm(
        'ATENÇÃO!!! Finalizar o lançamento do inventário?',
        {
          title: 'Finalizar inventário',
          okTitle: 'Fechar inventário',
          cancelTitle: 'Cancelar',
          centered: true,
          headerClass: 'bg-warning text-dark'
        }
      )

      if (!confirmar) return

      this.processando = true

      try {
        const { data } = await this.$http.post('/api/protected/estoqueInventario/fechar', {
          dataInventario: this.filtro.dataInventario
        })

        Tools.exibirMensagem(this.$store, 'Sucesso', 'success', data.mensagem || 'Inventário fechado com sucesso!')
        this.alterarData()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err.response?.data || err.message)
      } finally {
        this.processando = false
      }
    }
  }
}
</script>

<style scoped>
.list-header-premium {
  border-radius: 18px;
}

.header-icon-wrapper {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  font-size: 1.4rem;
}

.search-pill-responsive {
  min-height: 58px;
}

.inventory-date-wrapper {
  min-width: 300px;
}

.inventory-filter-wrapper {
  min-width: 220px;
}

.inventory-search-input {
  min-width: 210px;
}

.inventory-empty-state {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.empty-state-icon {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(32, 168, 216, 0.12) 0%, rgba(23, 131, 174, 0.2) 100%);
  color: #1783ae;
  font-size: 2rem;
}

.inventory-summary-card {
  border-radius: 18px;
  padding: 1.15rem 1.25rem;
  color: white;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.08);
}

.inventory-summary-primary {
  background: linear-gradient(135deg, #0b7285 0%, #1098ad 100%);
}

.inventory-summary-warning {
  background: linear-gradient(135deg, #e67700 0%, #f08c00 100%);
}

.inventory-summary-success {
  background: linear-gradient(135deg, #2b8a3e 0%, #37b24d 100%);
}

.summary-label {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.8;
  margin-bottom: 0.45rem;
}

.table-finance thead th {
  text-transform: uppercase;
  font-size: 0.7rem;
  font-weight: 700;
  color: #495057;
  letter-spacing: 0.5px;
  border-top: none !important;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 6px !important;
}

.table-finance tbody td {
  vertical-align: middle !important;
  padding: 6px !important;
  color: #4a5568;
  font-size: 0.85rem;
}

.table-finance tr:hover {
  background-color: rgba(32, 168, 216, 0.05) !important;
}

.btn-icon-only {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.btn-soft-info {
  background-color: #e0f7fa;
  color: #17a2b8;
  border: none;
}

.btn-soft-info:hover {
  background-color: #17a2b8;
  color: white;
}

.btn-soft-warning {
  background-color: #fff9db;
  color: #f0ad4e;
  border: none;
}

.btn-soft-warning:hover {
  background-color: #f0ad4e;
  color: white;
}

@media (max-width: 991.98px) {
  .inventory-date-wrapper,
  .inventory-filter-wrapper,
  .inventory-search-input {
    min-width: 100%;
    width: 100%;
  }

  .search-pill-responsive {
    width: 100%;
    border-radius: 18px !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/views/estoque/inventario/InventarioProdutosCadastro.vue